import React from 'react';
import {
  Check, Mail, Plus, Trash2, RefreshCw,
  Mic, Calendar, Edit, Loader2, User, Lock, ChevronDown, ChevronUp
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../ui/avatar';

const GuestGuidesList = ({
  loading,
  guides,
  viewMode,
  getStatusBadge,
  getInviteStageBadge,
  onAddGuide,
  onOpenEditModal,
  onOpenEmailModal,
  onOpenScheduleModal,
  onDeleteGuide,
  onMarkComplete,
  onReactivate,
  selectedGuideIds = [],
  onToggleGuideSelection,
  activeCampaignGuideIds = new Set(),
  suppressedGuideDeliveryById = new Map(),
  guideSections = [],
  expandedGuideSections = {},
  onToggleGuideSection,
}) => {
  const isSelected = (guideId) => selectedGuideIds.includes(guideId);
  const isQueued = (guideId) => activeCampaignGuideIds?.has?.(guideId);
  const getSuppressedDelivery = (guideId) => suppressedGuideDeliveryById?.get?.(guideId) || null;
  const getDeliveryIssueBadgeClasses = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === 'suppressed') {
      return 'border-amber-400/30 bg-amber-400/10 text-amber-200';
    }
    if (normalized === 'bounced') {
      return 'border-orange-400/30 bg-orange-400/10 text-orange-200';
    }
    return 'border-slate-400/30 bg-slate-400/10 text-slate-200';
  };
  const getGuideInitials = (guide) => {
    const name = (guide?.name || '').trim();
    if (!name) return 'GG';
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  };
  const shouldShowInviteStageBadge = (guide) => {
    const inviteStage = guide.invite_stage || 'not_invited';
    return inviteStage !== guide.status;
  };
  const renderGridCard = (guide) => {
    const suppressedDelivery = getSuppressedDelivery(guide.guide_id);
    return (
      <Card key={guide.guide_id} className={`bg-charcoal-light border-charcoal-lighter p-3 transition-colors hover:border-aqua/30 ${
        isSelected(guide.guide_id) ? 'ring-1 ring-aqua/70 border-aqua/50' : ''
      }`}>
        <div className="mb-3 flex items-center justify-end">
          <button
            type="button"
            onClick={() => onToggleGuideSelection?.(guide.guide_id)}
            disabled={isQueued(guide.guide_id)}
            className={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${
              isSelected(guide.guide_id)
                ? 'border-aqua bg-aqua/15 text-aqua'
                : isQueued(guide.guide_id)
                  ? 'cursor-not-allowed border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300'
                  : 'border-charcoal-lighter bg-charcoal text-foreground/75 hover:border-aqua/40 hover:text-foreground'
            }`}
          >
            {isSelected(guide.guide_id) ? 'Selected' : isQueued(guide.guide_id) ? 'Queued' : 'Select'}
          </button>
        </div>
        <div className="flex items-start gap-3">
          <Avatar className="h-14 w-14 shrink-0 rounded-xl border border-charcoal-lighter">
            {guide.photo_url ? <AvatarImage src={guide.photo_url} alt={guide.name} /> : null}
            <AvatarFallback className="rounded-xl bg-slate-200 text-slate-600 dark:bg-charcoal dark:text-slate-300">
              {guide.photo_url ? getGuideInitials(guide) : <User className="h-5 w-5" />}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap gap-1">
              {getStatusBadge(guide.status)}
              {shouldShowInviteStageBadge(guide) ? getInviteStageBadge(guide.invite_stage) : null}
              {isQueued(guide.guide_id) ? (
                <Badge className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                  <Lock className="mr-1 h-3 w-3" />
                  In queued sequence
                </Badge>
              ) : null}
              {suppressedDelivery ? (
                <Badge className={getDeliveryIssueBadgeClasses(suppressedDelivery.status)}>
                  {suppressedDelivery.failure_label || 'Delivery issue'}
                </Badge>
              ) : null}
            </div>
            <h3 className="mt-2 truncate text-sm font-semibold text-white">{guide.name}</h3>
            <p className="truncate text-xs text-aqua">{guide.role}</p>
            {guide.email && <p className="mt-1 truncate text-xs text-gray-400">{guide.email}</p>}
            {suppressedDelivery?.error ? (
              <p className="mt-1 line-clamp-2 text-[11px] text-amber-200/80">
                {suppressedDelivery.error}
              </p>
            ) : null}
          </div>
        </div>
        {(guide.focus_areas?.length > 0 || guide.scheduled_date) && (
          <div className="mt-3 space-y-2">
            {guide.focus_areas?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {guide.focus_areas.slice(0, 2).map((area, i) => (
                  <Badge key={i} className="border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700 dark:border-charcoal-lighter dark:bg-charcoal dark:text-gray-200">{area}</Badge>
                ))}
                {guide.focus_areas.length > 2 && (
                  <Badge className="border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700 dark:border-charcoal-lighter dark:bg-charcoal dark:text-gray-200">+{guide.focus_areas.length - 2}</Badge>
                )}
              </div>
            )}
            {guide.scheduled_date && (
              <p className="flex items-center gap-1 text-[11px] text-purple-400">
                <Calendar className="h-3 w-3" />
                {new Date(guide.scheduled_date).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
        <div className="mt-3 flex gap-1 border-t border-charcoal-lighter pt-3">
          <button onClick={() => onOpenEditModal(guide)} className="flex-1 rounded p-2 text-gray-400 transition-colors hover:bg-charcoal hover:text-white">
            <Edit className="mx-auto h-4 w-4" />
          </button>
          <button onClick={() => onOpenEmailModal(guide)} className="flex-1 rounded p-2 text-gray-400 transition-colors hover:bg-charcoal hover:text-aqua"
            disabled={!guide.email}>
            <Mail className="mx-auto h-4 w-4" />
          </button>
          <button onClick={() => onOpenScheduleModal(guide)} className="flex-1 rounded p-2 text-gray-400 transition-colors hover:bg-charcoal hover:text-purple-400">
            <Calendar className="mx-auto h-4 w-4" />
          </button>
          <button onClick={() => onDeleteGuide(guide.guide_id)} className="flex-1 rounded p-2 text-gray-400 transition-colors hover:bg-charcoal hover:text-red-400">
            <Trash2 className="mx-auto h-4 w-4" />
          </button>
        </div>
      </Card>
    );
  };

  const renderTableRow = (guide) => {
    const suppressedDelivery = getSuppressedDelivery(guide.guide_id);
    return (
      <tr key={guide.guide_id} className="hover:bg-charcoal/50">
        <td className="px-4 py-3">
          <input
            type="checkbox"
            checked={isSelected(guide.guide_id)}
            onChange={() => onToggleGuideSelection?.(guide.guide_id)}
            disabled={isQueued(guide.guide_id)}
            className="h-4 w-4 rounded border-charcoal-lighter bg-charcoal text-aqua focus:ring-aqua"
          />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              {guide.photo_url ? <AvatarImage src={guide.photo_url} alt={guide.name} /> : null}
              <AvatarFallback className="bg-slate-200 text-slate-600 dark:bg-charcoal dark:text-slate-300">
                {guide.photo_url ? getGuideInitials(guide) : <User className="h-4 w-4" />}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-white font-medium">{guide.name}</p>
              <p className="text-gray-400 text-sm">{guide.role}</p>
              {suppressedDelivery ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  <Badge className={getDeliveryIssueBadgeClasses(suppressedDelivery.status)}>
                    {suppressedDelivery.failure_label || 'Delivery issue'}
                  </Badge>
                </div>
              ) : null}
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <p className="text-gray-300 text-sm">{guide.email || '-'}</p>
          {suppressedDelivery?.error ? (
            <p className="mt-1 text-xs text-amber-200/80">{suppressedDelivery.error}</p>
          ) : null}
          <p className="text-gray-500 text-xs">{guide.phone || ''}</p>
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {guide.focus_areas?.slice(0, 2).map((area, i) => (
              <Badge key={i} className="border-slate-200 bg-slate-100 text-xs text-slate-700 dark:border-charcoal-lighter dark:bg-charcoal dark:text-gray-200">{area}</Badge>
            ))}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {getStatusBadge(guide.status)}
            {shouldShowInviteStageBadge(guide) ? getInviteStageBadge(guide.invite_stage) : null}
            {isQueued(guide.guide_id) ? (
              <Badge className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                <Lock className="mr-1 h-3 w-3" />
                Queued sequence
              </Badge>
            ) : null}
          </div>
        </td>
        <td className="px-4 py-3">
          {guide.scheduled_date ? (
            <span className="text-purple-400 text-sm">{new Date(guide.scheduled_date).toLocaleDateString()}</span>
          ) : (
            <span className="text-gray-500 text-sm">-</span>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex gap-1">
            <button onClick={() => onOpenEditModal(guide)} className="p-2 text-gray-400 hover:text-white rounded">
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={() => onOpenEmailModal(guide)} className="p-2 text-gray-400 hover:text-aqua rounded" disabled={!guide.email}>
              <Mail className="w-4 h-4" />
            </button>
            <button onClick={() => onOpenScheduleModal(guide)} className="p-2 text-gray-400 hover:text-purple-400 rounded">
              <Calendar className="w-4 h-4" />
            </button>
            {guide.status === 'scheduled' && (
              <button onClick={() => onMarkComplete(guide)} className="p-2 text-gray-400 hover:text-green-400 rounded">
                <Check className="w-4 h-4" />
              </button>
            )}
            {guide.status === 'completed' && (
              <button onClick={() => onReactivate(guide)} className="p-2 text-gray-400 hover:text-blue-400 rounded">
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => onDeleteGuide(guide.guide_id)} className="p-2 text-gray-400 hover:text-red-400 rounded">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-aqua animate-spin" />
      </div>
    );
  }

  if (guides.length === 0) {
    return (
      <Card className="bg-charcoal-light border-charcoal-lighter p-12 text-center">
        <Mic className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg text-white mb-2">No Guide Partners found</h3>
        <p className="text-gray-400 mb-4">Add your first Guide Partner or import a list</p>
        <Button onClick={onAddGuide} className="bg-aqua hover:bg-aqua-dark text-charcoal">
          <Plus className="w-4 h-4 mr-2" />
          Add Guide Partner
        </Button>
      </Card>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div className="space-y-4">
        {guideSections.map((section) => {
          const expanded = expandedGuideSections?.[section.key] ?? true;
          return (
            <Card key={section.key} className="border-charcoal-lighter bg-charcoal p-4">
              <button
                type="button"
                onClick={() => onToggleGuideSection?.(section.key)}
                className="flex w-full items-center justify-between gap-4 text-left"
              >
                <div>
                  <h3 className="text-base font-semibold text-white">{section.title}</h3>
                  <p className="mt-1 text-sm text-gray-400">{section.guides.length} guide{section.guides.length === 1 ? '' : 's'}</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <span>{expanded ? 'Collapse' : 'Expand'}</span>
                  {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </button>
              {expanded ? (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                  {section.guides.map(renderGridCard)}
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {guideSections.map((section) => {
        const expanded = expandedGuideSections?.[section.key] ?? true;
        return (
          <Card key={section.key} className="bg-charcoal-light border-charcoal-lighter overflow-hidden">
            <button
              type="button"
              onClick={() => onToggleGuideSection?.(section.key)}
              className="flex w-full items-center justify-between gap-4 border-b border-charcoal-lighter bg-charcoal px-4 py-4 text-left"
            >
              <div>
                <h3 className="text-base font-semibold text-white">{section.title}</h3>
                <p className="mt-1 text-sm text-gray-400">{section.guides.length} guide{section.guides.length === 1 ? '' : 's'}</p>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <span>{expanded ? 'Collapse' : 'Expand'}</span>
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </button>
            {expanded ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-charcoal">
                    <tr className="text-left text-gray-400 text-sm">
                      <th className="px-4 py-3">Select</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Contact</th>
                      <th className="px-4 py-3">Focus Areas</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Scheduled</th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-lighter">
                    {section.guides.map(renderTableRow)}
                  </tbody>
                </table>
              </div>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
};

export default GuestGuidesList;
